// SosLocationModal — shows one SOS alert's location on a map, with
// Acknowledge/Resolve actions. Uses Leaflet + OpenStreetMap (free, no API
// key), same stack as MapPickerModal.jsx and GuardiansMapPanel.jsx — not
// the Google Static Maps API, which (like Places) requires a billing-
// enabled Maps Platform project that this project deliberately doesn't have.

import { useEffect, useRef } from 'react';

export default function SosLocationModal({ alert, onClose, onAcknowledge, onResolve }) {
  const mapRef     = useRef(null);
  const leafletMap = useRef(null);

  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id   = 'leaflet-css';
      link.rel  = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;
    import('leaflet').then(L => {
      if (cancelled) return;
      const lat = Number(alert.latitude);
      const lng = Number(alert.longitude);
      const map = L.map(mapRef.current).setView([lat, lng], 15);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
      }).addTo(map);
      const icon = L.divIcon({
        className: '',
        html: '<div style="font-size:32px;line-height:1;margin-top:-28px;margin-left:-12px">🚨</div>',
        iconSize: [24, 32],
      });
      L.marker([lat, lng], { icon }).addTo(map);
      leafletMap.current = map;
    });
    return () => {
      cancelled = true;
      if (leafletMap.current) { leafletMap.current.remove(); leafletMap.current = null; }
    };
  }, [alert]);

  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-[1100]" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-paper-raised rounded-l shadow-2 w-[560px] max-w-[95vw] max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-4 py-3.5 border-b border-line-soft">
          <span className="font-display font-bold text-base text-ink">🚨 SOS Alert Location</span>
          <button onClick={onClose} className="bg-transparent border-none text-lg cursor-pointer text-ink-soft hover:text-ink">✕</button>
        </div>

        <div ref={mapRef} style={{ height: 320 }} />

        <div className="p-4 flex flex-col gap-1.5 text-sm text-ink">
          <div><strong>Driver:</strong> {alert.driver_name || 'Unknown'}</div>
          <div><strong>Reason:</strong> {alert.message}</div>
          <div><strong>Triggered:</strong> {new Date(alert.triggered_at).toLocaleString()}</div>
          <div className="flex items-center gap-2">
            <strong>Coordinates:</strong>
            <code className="bg-paper border border-line rounded px-1.5 py-0.5 text-xs">{Number(alert.latitude).toFixed(6)}, {Number(alert.longitude).toFixed(6)}</code>
          </div>
        </div>

        <div className="flex gap-2 px-4 py-3 border-t border-line-soft">
          {alert.status === 'active' && (
            <button className="bg-caution-tint text-caution font-bold px-4 py-2 rounded-s" onClick={() => onAcknowledge(alert.id)}>✓ Acknowledge</button>
          )}
          {alert.status !== 'resolved' && (
            <button className="bg-success-tint text-success font-bold px-4 py-2 rounded-s" onClick={() => onResolve(alert.id)}>✓ Resolve</button>
          )}
          <button className="bg-line-soft text-ink px-4 py-2 rounded-s ml-auto" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
